import type { jsPDF } from 'jspdf';
import { bookingSourceLabel } from '@/calendar/bookingSource';
import {
  airbnbStayKind,
  bookingOccupancyLabel,
  isIcalMachineLabel,
} from '@/calendar/bookingListDisplay';
import { bookingStatusLabel } from '@/calendar/pwaBookingDetail';
import {
  getBookingsForDay,
  getMonthGrid,
  isInternalCalendarBooking,
  preferInternalBookings,
  type PwaCalendarBooking,
} from '@/calendar/pwaCalendarUtils';

export type MonthPdfResource = {
  id: string;
  name: string;
  color?: string | null;
};

export type MonthBookingsPdfInput = {
  companyName: string;
  logoSrc: string;
  monthLabel: string;
  year: number;
  month: number;
  resourceLabel: string | null;
  resources: MonthPdfResource[];
  bookings: PwaCalendarBooking[];
};

type LogoRaster = {
  dataUrl: string;
  aspect: number;
};

type Rgb = [number, number, number];

type FlowBlock = {
  h: number;
  keepWithNext?: boolean;
  draw: (y: number) => void;
};

type Slot = { page: number; y: number };

type ResourceGroup = {
  id: string;
  name: string;
  color: string | null;
  bookings: PwaCalendarBooking[];
};

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 14;
const CONTENT_W = PAGE_W - MARGIN * 2;
const FOOTER_TOP = PAGE_H - 14;
const HEADER_H = 22;
const FIRST_BASELINE = HEADER_H + 10;
const INDEX_TITLE_H = 16;
const ROW_H = 8;
const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const PALETTE = ['#0F6E56', '#1D4ED8', '#C2410C', '#7C3AED', '#0F766E', '#BE123C'];

function pdfText(value: string): string {
  return value
    .replace(/\u2026/g, '...')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u00A0\u202F]/g, ' ')
    .replace(/[^\n\r\t\u0020-\u00FF]/g, '')
    .trim();
}

function fileSlug(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

function dayParts(booking: PwaCalendarBooking, iso: string): { day: number; month: number; year: number } | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  if (isInternalCalendarBooking(booking)) {
    return { day: date.getDate(), month: date.getMonth() + 1, year: date.getFullYear() };
  }
  return { day: date.getUTCDate(), month: date.getUTCMonth() + 1, year: date.getUTCFullYear() };
}

function formatBookingDay(booking: PwaCalendarBooking, iso: string): string {
  const parts = dayParts(booking, iso);
  if (!parts) return '-';
  return `${String(parts.day).padStart(2, '0')}/${String(parts.month).padStart(2, '0')}/${parts.year}`;
}

function isArrivalDay(booking: PwaCalendarBooking, date: Date): boolean {
  const parts = dayParts(booking, booking.startDate);
  if (!parts) return false;
  return parts.year === date.getFullYear() && parts.month === date.getMonth() + 1 && parts.day === date.getDate();
}

function nightsBetween(booking: PwaCalendarBooking): number {
  const start = dayParts(booking, booking.startDate);
  const end = dayParts(booking, booking.endDate);
  if (!start || !end) return 0;
  const a = Date.UTC(start.year, start.month - 1, start.day);
  const b = Date.UTC(end.year, end.month - 1, end.day);
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

function formatMoney(cents: number | null | undefined, currency: string | null | undefined): string | null {
  if (cents == null || !Number.isFinite(cents)) return null;
  const code = currency?.trim() || 'USD';
  try {
    return new Intl.NumberFormat('es-UY', { style: 'currency', currency: code })
      .format(cents / 100)
      .replace(/[\u00A0\u202F]/g, ' ');
  } catch {
    return `${(cents / 100).toFixed(2)} ${code}`;
  }
}

function hexToRgb(hex: string | null | undefined): Rgb {
  if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return [15, 110, 86];
  return [
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16),
  ];
}

function mixWhite(rgb: Rgb, amount: number): Rgb {
  return rgb.map((channel) => Math.round(channel + (255 - channel) * amount)) as Rgb;
}

function fitText(doc: jsPDF, text: string, maxWidth: number): string {
  const clean = pdfText(text);
  if (doc.getTextWidth(clean) <= maxWidth) return clean;
  let cut = clean;
  while (cut.length > 1 && doc.getTextWidth(`${cut}...`) > maxWidth) {
    cut = cut.slice(0, -1);
  }
  return `${cut}...`;
}

function linesOf(doc: jsPDF, text: string, width: number): string[] {
  const lines = doc.splitTextToSize(pdfText(text), width) as string[];
  return lines.length > 0 ? lines : [''];
}

function guestLabel(booking: PwaCalendarBooking): string {
  if (isIcalMachineLabel(booking)) return bookingOccupancyLabel(booking);
  return booking.customerName?.trim() || 'Sin nombre';
}

function calendarCellLines(booking: PwaCalendarBooking): { text: string; bold: boolean }[] {
  const kind = airbnbStayKind(booking);
  if (kind === 'reserved') return [{ text: 'Airbnb', bold: true }];
  if (kind === 'block') return [{ text: 'Bloqueo', bold: false }];
  const channel = bookingSourceLabel(booking);
  const code = booking.publicReference?.trim();
  if (!isInternalCalendarBooking(booking) || !code) return [{ text: channel, bold: false }];
  return [
    { text: channel, bold: true },
    { text: code, bold: false },
  ];
}

function stayLabel(booking: PwaCalendarBooking): string {
  const nights = nightsBetween(booking);
  const parts = [`Del ${formatBookingDay(booking, booking.startDate)} al ${formatBookingDay(booking, booking.endDate)}`];
  if (nights === 1) parts.push('1 noche');
  else if (nights > 1) parts.push(`${nights} noches`);
  if (booking.timeBlock?.trim()) parts.push(booking.timeBlock.trim());
  return parts.join(' · ');
}

function eachDate(year: number, month: number): Date[] {
  const count = new Date(year, month, 0).getDate();
  return Array.from({ length: count }, (_, index) => new Date(year, month - 1, index + 1));
}

function sortedBookings(bookings: PwaCalendarBooking[]): PwaCalendarBooking[] {
  const seen = new Set<string>();
  return bookings
    .filter((booking) => {
      if (seen.has(booking.id)) return false;
      seen.add(booking.id);
      return true;
    })
    .sort((a, b) => {
      const byDate = a.startDate.localeCompare(b.startDate);
      if (byDate !== 0) return byDate;
      return a.resourceName.localeCompare(b.resourceName, 'es');
    });
}

function bookingsOnDay(bookings: PwaCalendarBooking[], date: Date): PwaCalendarBooking[] {
  const covering = getBookingsForDay(bookings, date);
  const byResource = new Map<string, PwaCalendarBooking[]>();
  for (const booking of covering) {
    const key = booking.resourceId || booking.resourceName;
    const list = byResource.get(key) ?? [];
    list.push(booking);
    byResource.set(key, list);
  }
  return [...byResource.values()].flatMap((list) => preferInternalBookings(list));
}

function resourceGroups(input: MonthBookingsPdfInput, bookings: PwaCalendarBooking[]): ResourceGroup[] {
  const map = new Map<string, ResourceGroup>();
  input.resources.forEach((resource, index) => {
    map.set(resource.id, {
      id: resource.id,
      name: resource.name.trim() || 'Sin recurso',
      color: resource.color?.trim() || PALETTE[index % PALETTE.length],
      bookings: [],
    });
  });
  for (const booking of bookings) {
    const id = booking.resourceId || booking.resourceName;
    let group = map.get(id);
    if (!group) {
      group = {
        id,
        name: booking.resourceName?.trim() || 'Sin recurso',
        color: booking.resourceColor?.trim() || PALETTE[map.size % PALETTE.length],
        bookings: [],
      };
      map.set(id, group);
    }
    if (!group.color && booking.resourceColor) group.color = booking.resourceColor;
    group.bookings.push(booking);
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

function indexSlots(rowCount: number): Slot[] {
  let page = 1;
  let y = FIRST_BASELINE + INDEX_TITLE_H;
  const slots: Slot[] = [];
  for (let index = 0; index < rowCount; index += 1) {
    if (y + ROW_H > FOOTER_TOP) {
      page += 1;
      y = FIRST_BASELINE;
    }
    slots.push({ page, y });
    y += ROW_H;
  }
  return slots;
}

function placeBlocks(blocks: FlowBlock[], startPage: number): { slots: Slot[]; endPage: number } {
  let page = startPage;
  let y = FIRST_BASELINE;
  const slots: Slot[] = [];
  for (let index = 0; index < blocks.length; index += 1) {
    const nextH = blocks[index].keepWithNext && blocks[index + 1] ? blocks[index + 1].h : 0;
    const need = blocks[index].h + nextH;
    if (y + need > FOOTER_TOP && y > FIRST_BASELINE) {
      page += 1;
      y = FIRST_BASELINE;
    }
    slots.push({ page, y });
    y += blocks[index].h;
  }
  return { slots, endPage: blocks.length ? page : startPage };
}

async function loadLogo(src: string): Promise<LogoRaster | null> {
  try {
    const response = await fetch(src);
    if (!response.ok) return null;
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('logo'));
        img.src = objectUrl;
      });
      const maxEdge = 256;
      const scale = Math.min(maxEdge / image.naturalWidth, maxEdge / image.naturalHeight, 1);
      const width = Math.max(1, Math.round(image.naturalWidth * scale));
      const height = Math.max(1, Math.round(image.naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(image, 0, 0, width, height);
      return { dataUrl: canvas.toDataURL('image/png'), aspect: width / height };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    return null;
  }
}

function paintLogo(doc: jsPDF, logo: LogoRaster | null, x: number, y: number, box: number): void {
  if (!logo) return;
  let drawW = box;
  let drawH = box / logo.aspect;
  if (drawH > box) {
    drawH = box;
    drawW = box * logo.aspect;
  }
  doc.addImage(logo.dataUrl, 'PNG', x + (box - drawW) / 2, y + (box - drawH) / 2, drawW, drawH);
}

function drawHeader(doc: jsPDF, logo: LogoRaster | null, input: MonthBookingsPdfInput): void {
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, PAGE_W, HEADER_H, 'F');
  const box = 12;
  const boxY = (HEADER_H - box) / 2;
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(MARGIN, boxY, box, box, 1.6, 1.6, 'F');
  paintLogo(doc, logo, MARGIN + 0.7, boxY + 0.7, box - 1.4);

  const textX = MARGIN + box + 3.5;
  const company = pdfText(input.companyName.trim() || 'Empresa');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(fitText(doc, company, 88), textX, 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(203, 213, 225);
  doc.text('Reservas del calendario', textX, 15.5);

  const scope = input.resourceLabel?.trim() || 'Todos los recursos';
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(pdfText(input.monthLabel), PAGE_W - MARGIN, 10, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(203, 213, 225);
  doc.text(fitText(doc, scope, 70), PAGE_W - MARGIN, 15.5, { align: 'right' });
}

function drawSectionTitle(doc: jsPDF, title: string, subtitle: string, y: number): void {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(15, 23, 42);
  doc.text(fitText(doc, title, CONTENT_W), MARGIN, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(pdfText(subtitle), MARGIN, y + 6);
}

function dayHeading(date: Date): string {
  const raw = date.toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' });
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

type CardBlock = {
  guest: string;
  stay: string[];
  meta: string[];
  money: string | null;
  message: string[];
  accent: Rgb;
  height: number;
  guestBold: boolean;
};

function measureCard(doc: jsPDF, booking: PwaCalendarBooking, color: string | null): CardBlock {
  const innerW = CONTENT_W - 8;
  const guestBold = airbnbStayKind(booking) !== 'block';
  doc.setFont('helvetica', guestBold ? 'bold' : 'normal');
  doc.setFontSize(11);
  const guest = fitText(doc, guestLabel(booking), innerW);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const stay = linesOf(doc, stayLabel(booking), innerW);
  const metaParts = [bookingSourceLabel(booking), bookingStatusLabel(booking.status)];
  if (booking.resourceTypeName?.trim()) metaParts.push(booking.resourceTypeName.trim());
  if (booking.partySize && booking.partySize > 0) {
    metaParts.push(booking.partySize === 1 ? '1 persona' : `${booking.partySize} personas`);
  }
  if (booking.customerPhone?.trim()) metaParts.push(booking.customerPhone.trim());
  if (booking.customerEmail?.trim()) metaParts.push(booking.customerEmail.trim());
  if (booking.publicReference?.trim()) metaParts.push(`Código ${booking.publicReference.trim()}`);
  doc.setFontSize(8.5);
  const meta = linesOf(doc, metaParts.join(' · '), innerW);
  const money = formatMoney(booking.quotedTotalCents, booking.quotedCurrency);
  const preview = booking.sourceMessagePreview?.trim();
  const message =
    preview && !isIcalMachineLabel(booking) ? linesOf(doc, `Mensaje: ${preview}`, innerW).slice(0, 4) : [];
  const height =
    6 +
    5 +
    stay.length * 4.1 +
    meta.length * 3.7 +
    (money ? 4.4 : 0) +
    message.length * 3.7 +
    2.4;
  return { guest, stay, meta, money, message, accent: hexToRgb(color), height, guestBold };
}

function drawCard(doc: jsPDF, card: CardBlock, y: number): void {
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.2);
  doc.roundedRect(MARGIN, y, CONTENT_W, card.height, 1.6, 1.6, 'FD');
  doc.setFillColor(...card.accent);
  doc.rect(MARGIN, y + 1.4, 1.6, Math.max(2, card.height - 2.8), 'F');

  const x = MARGIN + 5;
  let cursor = y + 6;
  doc.setFont('helvetica', card.guestBold ? 'bold' : 'normal');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text(card.guest, x, cursor);
  cursor += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(51, 65, 85);
  for (const line of card.stay) {
    doc.text(line, x, cursor);
    cursor += 4.1;
  }
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  for (const line of card.meta) {
    doc.text(line, x, cursor);
    cursor += 3.7;
  }
  if (card.money) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    doc.text(pdfText(card.money), x, cursor);
    cursor += 4.4;
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  for (const line of card.message) {
    doc.text(line, x, cursor);
    cursor += 3.7;
  }
}

function drawContinuation(doc: jsPDF, booking: PwaCalendarBooking, color: string | null, y: number): void {
  const accent = hexToRgb(color);
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.15);
  doc.roundedRect(MARGIN, y, CONTENT_W, 6.6, 1.2, 1.2, 'FD');
  doc.setFillColor(...accent);
  doc.rect(MARGIN, y + 1.3, 1.6, 4, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(51, 65, 85);
  const text = `Continúa · ${guestLabel(booking)} · sale el ${formatBookingDay(booking, booking.endDate)}`;
  doc.text(fitText(doc, text, CONTENT_W - 10), MARGIN + 5, y + 4.4);
}

function summaryBlocks(doc: jsPDF, bookings: PwaCalendarBooking[], year: number, month: number): FlowBlock[] {
  const days = eachDate(year, month)
    .map((date) => ({ date, items: bookingsOnDay(bookings, date) }))
    .filter((day) => day.items.length > 0);
  const occupied = days.length;
  const subtitle =
    bookings.length === 0
      ? 'Sin reservas confirmadas'
      : `${bookings.length === 1 ? '1 reserva confirmada' : `${bookings.length} reservas confirmadas`} · ${
          occupied === 1 ? '1 día con ocupación' : `${occupied} días con ocupación`
        }`;
  const blocks: FlowBlock[] = [
    {
      h: 14,
      keepWithNext: true,
      draw: (y) => drawSectionTitle(doc, 'Resumen por día', subtitle, y),
    },
  ];
  if (days.length === 0) {
    blocks.push({
      h: 10,
      draw: (y) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(71, 85, 105);
        doc.text('No hay reservas confirmadas en este mes.', MARGIN, y);
      },
    });
    return blocks;
  }
  for (const day of days) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const lines = day.items.flatMap((booking) =>
      linesOf(
        doc,
        `${booking.resourceName} · ${bookingSourceLabel(booking)} · ${stayLabel(booking)}`,
        CONTENT_W - 4
      )
    );
    const height = 7 + lines.length * 4.3 + 2.4;
    blocks.push({
      h: height,
      draw: (y) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(15, 23, 42);
        doc.text(pdfText(dayHeading(day.date)), MARGIN, y);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(51, 65, 85);
        let cursor = y + 5.2;
        for (const line of lines) {
          doc.text(line, MARGIN + 2, cursor);
          cursor += 4.3;
        }
      },
    });
  }
  return blocks;
}

function detailBlocks(doc: jsPDF, group: ResourceGroup, year: number, month: number): FlowBlock[] {
  const days = eachDate(year, month)
    .map((date) => ({ date, items: bookingsOnDay(group.bookings, date) }))
    .filter((day) => day.items.length > 0);
  if (days.length === 0) return [];
  const blocks: FlowBlock[] = [
    {
      h: 14,
      keepWithNext: true,
      draw: (y) => drawSectionTitle(doc, group.name, 'Detalle día por día', y),
    },
  ];
  for (const day of days) {
    blocks.push({
      h: 8,
      keepWithNext: true,
      draw: (y) => {
        doc.setFillColor(241, 245, 249);
        doc.roundedRect(MARGIN, y - 4.2, CONTENT_W, 7.2, 1.2, 1.2, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(15, 23, 42);
        doc.text(pdfText(dayHeading(day.date)), MARGIN + 3, y);
      },
    });
    for (const booking of day.items) {
      const full = isArrivalDay(booking, day.date) || day.date.getDate() === 1;
      if (full) {
        const card = measureCard(doc, booking, group.color);
        blocks.push({
          h: card.height + 2.4,
          draw: (y) => drawCard(doc, card, y),
        });
      } else {
        blocks.push({
          h: 8.4,
          draw: (y) => drawContinuation(doc, booking, group.color, y),
        });
      }
    }
  }
  return blocks;
}

function drawCalendar(
  doc: jsPDF,
  group: ResourceGroup,
  year: number,
  month: number,
  y: number
): void {
  const nights = eachDate(year, month).filter((date) => bookingsOnDay(group.bookings, date).length > 0).length;
  const reservaLabel = group.bookings.length === 1 ? '1 reserva' : `${group.bookings.length} reservas`;
  const nightLabel = nights === 1 ? '1 noche ocupada' : `${nights} noches ocupadas`;
  drawSectionTitle(doc, group.name, `Calendario · ${reservaLabel} · ${nightLabel}`, y);

  const weeks = getMonthGrid(year, month);
  const gridTop = y + 12;
  const headerRow = 7;
  const gridBottom = FOOTER_TOP - 1;
  const rowH = (gridBottom - gridTop - headerRow) / weeks.length;
  const colW = CONTENT_W / 7;
  const accent = hexToRgb(group.color);
  const tint = mixWhite(accent, 0.78);
  const today = new Date();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  WEEKDAYS.forEach((label, index) => {
    doc.text(label, MARGIN + index * colW + colW / 2, gridTop + 4.5, { align: 'center' });
  });

  weeks.forEach((week, row) => {
    week.forEach((cell, col) => {
      const x = MARGIN + col * colW;
      const cellY = gridTop + headerRow + row * rowH;
      const inMonth = cell.isCurrentMonth;
      const dayBookings = inMonth ? bookingsOnDay(group.bookings, cell.date) : [];
      const occupied = dayBookings.length > 0;
      if (!inMonth) doc.setFillColor(248, 250, 252);
      else if (occupied) doc.setFillColor(...tint);
      else doc.setFillColor(255, 255, 255);
      doc.rect(x, cellY, colW, rowH, 'F');

      const isToday =
        inMonth &&
        cell.date.getFullYear() === today.getFullYear() &&
        cell.date.getMonth() === today.getMonth() &&
        cell.date.getDate() === today.getDate();
      doc.setDrawColor(isToday ? 15 : 226, isToday ? 23 : 232, isToday ? 42 : 240);
      doc.setLineWidth(isToday ? 0.45 : 0.15);
      doc.rect(x, cellY, colW, rowH, 'S');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      if (!inMonth) doc.setTextColor(203, 213, 225);
      else if (occupied) doc.setTextColor(...accent);
      else doc.setTextColor(15, 23, 42);
      doc.text(String(cell.day), x + 1.6, cellY + 4.2);

      if (!occupied) return;
      doc.setFontSize(6.5);
      doc.setTextColor(30, 41, 59);
      const lines = dayBookings.flatMap((booking) => calendarCellLines(booking));
      let cursor = cellY + 8.2;
      for (const line of lines) {
        if (cursor > cellY + rowH - 1.6) break;
        doc.setFont('helvetica', line.bold ? 'bold' : 'normal');
        doc.text(fitText(doc, line.text, colW - 3.2), x + 1.6, cursor);
        cursor += 3.2;
      }
    });
  });
}

function drawFlow(
  doc: jsPDF,
  logo: LogoRaster | null,
  input: MonthBookingsPdfInput,
  blocks: FlowBlock[],
  slots: Slot[],
  footerLabel: string,
  footerByPage: string[]
): void {
  for (let index = 0; index < blocks.length; index += 1) {
    const slot = slots[index];
    while (doc.getNumberOfPages() < slot.page) {
      doc.addPage();
      drawHeader(doc, logo, input);
      footerByPage[doc.getNumberOfPages()] = footerLabel;
    }
    doc.setPage(slot.page);
    blocks[index].draw(slot.y);
  }
}

function drawIndex(
  doc: jsPDF,
  logo: LogoRaster | null,
  input: MonthBookingsPdfInput,
  entries: { label: string; page: number }[],
  slots: Slot[],
  generated: string,
  footerByPage: string[]
): void {
  drawHeader(doc, logo, input);
  footerByPage[1] = 'Índice';
  doc.setPage(1);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(15, 23, 42);
  doc.text('Índice', MARGIN, FIRST_BASELINE);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(pdfText(`Generado ${generated}`), MARGIN, FIRST_BASELINE + 6);

  entries.forEach((entry, index) => {
    const slot = slots[index];
    while (doc.getNumberOfPages() < slot.page) {
      doc.addPage();
      drawHeader(doc, logo, input);
      footerByPage[doc.getNumberOfPages()] = 'Índice';
    }
    doc.setPage(slot.page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    const title = fitText(doc, entry.label, 150);
    const titleW = doc.getTextWidth(title);
    doc.text(title, MARGIN, slot.y);
    const pageText = String(entry.page);
    doc.setFont('helvetica', 'bold');
    const pageW = doc.getTextWidth(pageText);
    doc.text(pageText, PAGE_W - MARGIN, slot.y, { align: 'right' });
    const left = MARGIN + titleW + 2;
    const right = PAGE_W - MARGIN - pageW - 2;
    if (right > left + 4) {
      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.25);
      doc.setLineDashPattern([0.4, 1.2], 0);
      doc.line(left, slot.y - 1, right, slot.y - 1);
      doc.setLineDashPattern([], 0);
    }
    doc.link(MARGIN, slot.y - 5, CONTENT_W, ROW_H, { pageNumber: entry.page });
  });
}

/** En el teléfono el guardado pasa por el menú de compartir. En el escritorio se descarga. */
function isHandheld(): boolean {
  const ua = navigator.userAgent;
  if (/Android|iPhone|iPod|iPad/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

async function deliverPdf(doc: jsPDF, filename: string, monthLabel: string): Promise<void> {
  if (isHandheld() && typeof navigator.share === 'function' && typeof navigator.canShare === 'function') {
    const blob = doc.output('blob');
    const file = new File([blob], filename, { type: 'application/pdf' });
    if (navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: `Reservas ${monthLabel}` });
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
      }
    }
  }
  doc.save(filename);
}

export async function downloadMonthBookingsPdf(input: MonthBookingsPdfInput): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const logo = await loadLogo(input.logoSrc);
  const bookings = sortedBookings(input.bookings);
  const groups = resourceGroups(input, bookings);
  const company = input.companyName.trim() || 'Empresa';
  doc.setProperties({
    title: `Reservas ${input.monthLabel}`,
    subject: company,
    creator: company,
  });

  const summary = summaryBlocks(doc, bookings, input.year, input.month);
  const details = groups.map((group) => detailBlocks(doc, group, input.year, input.month));

  const tocLabels = ['Resumen por día', ...groups.map((group) => group.name)];
  const tocSlots = indexSlots(tocLabels.length);
  const indexPages = tocSlots.at(-1)?.page ?? 1;
  let page = indexPages + 1;
  const summaryPlaced = placeBlocks(summary, page);
  page = summaryPlaced.endPage;
  const groupPlans = details.map((blocks) => {
    page += 1;
    const calendarPage = page;
    if (blocks.length === 0) return { calendarPage, placed: null as ReturnType<typeof placeBlocks> | null };
    page += 1;
    const placed = placeBlocks(blocks, page);
    page = placed.endPage;
    return { calendarPage, placed };
  });
  const toc = [
    { label: 'Resumen por día', page: summaryPlaced.slots[0]?.page ?? indexPages + 1 },
    ...groups.map((group, index) => ({ label: group.name, page: groupPlans[index].calendarPage })),
  ];

  const generated = new Date().toLocaleString('es', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const footerByPage: string[] = [];
  drawIndex(doc, logo, input, toc, tocSlots, generated, footerByPage);
  drawFlow(doc, logo, input, summary, summaryPlaced.slots, 'Resumen por día', footerByPage);

  groups.forEach((group, index) => {
    const plan = groupPlans[index];
    while (doc.getNumberOfPages() < plan.calendarPage) {
      doc.addPage();
      drawHeader(doc, logo, input);
      footerByPage[doc.getNumberOfPages()] = group.name;
    }
    doc.setPage(plan.calendarPage);
    drawCalendar(doc, group, input.year, input.month, FIRST_BASELINE);
    if (plan.placed) {
      drawFlow(doc, logo, input, details[index], plan.placed.slots, `${group.name} · detalle`, footerByPage);
    }
  });

  const total = doc.getNumberOfPages();
  for (let number = 1; number <= total; number += 1) {
    doc.setPage(number);
    const section = footerByPage[number] ?? '';
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    const left = pdfText(section ? `${company} · ${section}` : company);
    doc.text(left, MARGIN, PAGE_H - 7);
    doc.text(`${number} / ${total}`, PAGE_W - MARGIN, PAGE_H - 7, { align: 'right' });
  }

  const monthSlug = fileSlug(input.monthLabel) || 'mes';
  const resourceSlug = input.resourceLabel ? fileSlug(input.resourceLabel) : '';
  const filename = resourceSlug ? `reservas-${resourceSlug}-${monthSlug}.pdf` : `reservas-${monthSlug}.pdf`;
  await deliverPdf(doc, filename, input.monthLabel);
}
